import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Card from "../../../components/Card";
import { postApiCall } from "../../../service/httpCall";
import { API_DISPOSITION_CREATE } from "../../../service/url";
import { getUser } from "../../../utilities/setting";
import { MODULE_ACL, ROUTES, USER_ROLES } from "../../../utilities/constant";

const NAME_REGEX = /^[a-zA-Z0-9 _-]+$/;

const FIELD_TYPES = ["String", "Integer", "Float", "Date"];

const emptyField = () => ({
  field_name: "",
  field_type: "String",
  is_required: false,
  enable_default: false,
  enable_min: false,
  enable_max: false,
  field_default: "",
  field_min: "",
  field_max: "",
});

const normalizeField = (field) => {
  const fieldType = field?.field_type || "String";

  const toNullableNumber = (val, { integerOnly } = {}) => {
    if (val === null || val === undefined || val === "") return null;
    const num = Number(val);
    if (!Number.isFinite(num)) return null;
    if (integerOnly && !Number.isInteger(num)) return null;
    return num;
  };

  const toNullableDateString = (val) => {
    if (!val) return null;
    const date = new Date(val);
    if (Number.isNaN(date.getTime())) return null;
    return String(val);
  };

  const toNullableDefault = () => {
    if (!field?.enable_default) return null;
    if (fieldType === "Integer") return toNullableNumber(field?.field_default, { integerOnly: true });
    if (fieldType === "Float") return toNullableNumber(field?.field_default);
    if (fieldType === "Date") return toNullableDateString(field?.field_default);
    return String(field?.field_default ?? "").trim() || null;
  };

  const toNullableMin = () => {
    if (fieldType === "String") return null;
    if (!field?.enable_min) return null;
    if (fieldType === "Integer") return toNullableNumber(field?.field_min, { integerOnly: true });
    if (fieldType === "Float") return toNullableNumber(field?.field_min);
    if (fieldType === "Date") return toNullableDateString(field?.field_min);
    return toNullableNumber(field?.field_min, { integerOnly: true });
  };

  const toNullableMax = () => {
    if (fieldType === "String") return null;
    if (!field?.enable_max) return null;
    if (fieldType === "Integer") return toNullableNumber(field?.field_max, { integerOnly: true });
    if (fieldType === "Float") return toNullableNumber(field?.field_max);
    if (fieldType === "Date") return toNullableDateString(field?.field_max);
    return toNullableNumber(field?.field_max, { integerOnly: true });
  };

  return {
    field_name: String(field?.field_name || "").trim(),
    field_type: fieldType,
    is_required: Boolean(field?.is_required),
    field_default: toNullableDefault(),
    field_min: toNullableMin(),
    field_max: toNullableMax(),
  };
};

const DispositionFormsCreate = () => {
  const navigate = useNavigate();
  const [buttonDisable, setButtonDisable] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);

  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState({});

  const [name, setName] = useState("");
  const [fields, setFields] = useState([emptyField()]);

  useEffect(() => {
    getUser().then((user) => {
      if (!user) return;
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
      if (user?.acl_settings) {
        setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
      }
      setUserLoaded(true);
    });
  }, []);

  const canAdd = useMemo(
    () => isSuperAdmin || loggedInUserACL?.[MODULE_ACL.DISPOSITION_FORMS]?.add,
    [isSuperAdmin, loggedInUserACL],
  );

  useEffect(() => {
    if (!userLoaded) return;
    if (!canAdd) {
      toast.error("You don't have permission to create disposition forms");
      navigate(`/${ROUTES.DISPOSITION_FORMS.LIST}`);
    }
  }, [canAdd, navigate, userLoaded]);

  const updateField = (index, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      const existing = next[index] || emptyField();
      const updated = { ...existing, [key]: value };
      if (key === "field_type") {
        updated.enable_default = false;
        updated.enable_min = false;
        updated.enable_max = false;
        updated.field_default = "";
        updated.field_min = "";
        updated.field_max = "";
      }
      next[index] = updated;
      return next;
    });
  };

  const addField = () => setFields((prev) => [...prev, emptyField()]);

  const removeField = (index) =>
    setFields((prev) => prev.filter((_, idx) => idx !== index));

  const getValueMeta = (fieldType) => {
    if (fieldType === "Integer") return { inputType: "number", step: 1, placeholder: "Integer" };
    if (fieldType === "Float") return { inputType: "number", step: "any", placeholder: "Number" };
    if (fieldType === "Date") return { inputType: "date", step: undefined, placeholder: "" };
    return { inputType: "text", step: undefined, placeholder: "Text" };
  };

  const getMinMaxMeta = (fieldType) => {
    if (fieldType === "Integer") return { inputType: "number", step: 1, placeholder: "Integer" };
    if (fieldType === "Float") return { inputType: "number", step: "any", placeholder: "Number" };
    if (fieldType === "Date") return { inputType: "date", step: undefined, placeholder: "" };
    return { inputType: "number", step: 1, placeholder: "Length" };
  };

  const validate = () => {
    if (!name.trim()) {
      toast.warning("Name is required");
      return false;
    }

    if (!Array.isArray(fields) || fields.length === 0) {
      toast.warning("At least one field is required");
      return false;
    }

    for (let i = 0; i < fields.length; i += 1) {
      const normalized = normalizeField(fields[i]);
      if (!normalized.field_name) {
        toast.warning(`Field name is required (row ${i + 1})`);
        return false;
      }
      if (!FIELD_TYPES.includes(normalized.field_type)) {
        toast.warning(`Invalid field type (row ${i + 1})`);
        return false;
      }
      if (fields[i]?.enable_default && normalized.field_default === null) {
        toast.warning(`Invalid default value (row ${i + 1})`);
        return false;
      }
      if (fields[i]?.enable_min && normalized.field_min === null) {
        toast.warning(`Invalid min value (row ${i + 1})`);
        return false;
      }
      if (fields[i]?.enable_max && normalized.field_max === null) {
        toast.warning(`Invalid max value (row ${i + 1})`);
        return false;
      }

      if (
        normalized.field_type !== "Date" &&
        typeof normalized.field_min === "number" &&
        typeof normalized.field_max === "number" &&
        normalized.field_min > normalized.field_max
      ) {
        toast.warning(`Min cannot be greater than max (row ${i + 1})`);
        return false;
      }

      if (
        normalized.field_type === "Date" &&
        normalized.field_min &&
        normalized.field_max &&
        new Date(normalized.field_min).getTime() > new Date(normalized.field_max).getTime()
      ) {
        toast.warning(`Min date cannot be after max date (row ${i + 1})`);
        return false;
      }
    }

    return true;
  };

  const submitForm = () => {
    if (!validate()) return;

    setButtonDisable(true);
    const payload = {
      name: name.trim(),
      fields: fields.map(normalizeField),
    };

    postApiCall(API_DISPOSITION_CREATE, payload)
      .then(() => {
        toast.success("Disposition form created successfully");
        setTimeout(() => navigate(`/${ROUTES.DISPOSITION_FORMS.LIST}`), 800);
      })
      .catch((err) => {
        toast.error(err || "Unable to create disposition form");
        setButtonDisable(false);
      });
  };

  if (!userLoaded || !canAdd) return null;

  return (
    <Row>
      <Col xl="12">
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4 className="card-title">Create Disposition Form</h4>
            <Button
              variant="secondary"
              onClick={() => navigate(`/${ROUTES.DISPOSITION_FORMS.LIST}`)}
            >
              Back
            </Button>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || NAME_REGEX.test(value)) {
                      setName(value);
                    }
                  }}
                  placeholder="Only letters, numbers, space, _ and - allowed"
                />
              </Form.Group>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Fields</h5>
                <Button variant="outline-primary" size="sm" onClick={addField}>
                  Add Field
                </Button>
              </div>

              {fields.map((field, idx) => {
                const fieldType = field?.field_type || "String";
                const defaultMeta = getValueMeta(fieldType);
                const minMaxMeta = getMinMaxMeta(fieldType);
                return (
                  <div key={idx} className="border rounded p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Field #{idx + 1}</strong>
                      {fields.length > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeField(idx)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <Row>
                      <Form.Group className="col-md-4 mb-3">
                        <Form.Label>
                          Field Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={field.field_name}
                          onChange={(e) =>
                            updateField(idx, "field_name", e.target.value)
                          }
                          placeholder="e.g. Customer Name"
                        />
                      </Form.Group>

                      <Form.Group className="col-md-3 mb-3">
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                          value={fieldType}
                          onChange={(e) =>
                            updateField(idx, "field_type", e.target.value)
                          }
                        >
                          {FIELD_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="col-md-2 mb-3 d-flex align-items-end">
                        <Form.Check
                          type="checkbox"
                          label="Required"
                          checked={Boolean(field.is_required)}
                          onChange={(e) =>
                            updateField(idx, "is_required", e.target.checked)
                          }
                        />
                      </Form.Group>

                    </Row>

                    <Row>
                      <Form.Group className="col-md-4 mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <Form.Label className="mb-0">Default</Form.Label>
                          <Form.Check
                            type="checkbox"
                            checked={Boolean(field.enable_default)}
                            onChange={(e) => updateField(idx, "enable_default", e.target.checked)}
                          />
                        </div>
                        <Form.Control
                          disabled={!field.enable_default}
                          type={defaultMeta.inputType}
                          step={defaultMeta.step}
                          value={field.field_default ?? ""}
                          onChange={(e) => updateField(idx, "field_default", e.target.value)}
                          placeholder={defaultMeta.placeholder}
                        />
                      </Form.Group>

                      {fieldType !== "String" && (
                        <>
                          <Form.Group className="col-md-4 mb-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <Form.Label className="mb-0">Min</Form.Label>
                              <Form.Check
                                type="checkbox"
                                checked={Boolean(field.enable_min)}
                                onChange={(e) =>
                                  updateField(idx, "enable_min", e.target.checked)
                                }
                              />
                            </div>
                            <Form.Control
                              disabled={!field.enable_min}
                              type={minMaxMeta.inputType}
                              step={minMaxMeta.step}
                              value={field.field_min ?? ""}
                              onChange={(e) =>
                                updateField(idx, "field_min", e.target.value)
                              }
                              placeholder={minMaxMeta.placeholder}
                            />
                          </Form.Group>

                          <Form.Group className="col-md-4 mb-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <Form.Label className="mb-0">Max</Form.Label>
                              <Form.Check
                                type="checkbox"
                                checked={Boolean(field.enable_max)}
                                onChange={(e) =>
                                  updateField(idx, "enable_max", e.target.checked)
                                }
                              />
                            </div>
                            <Form.Control
                              disabled={!field.enable_max}
                              type={minMaxMeta.inputType}
                              step={minMaxMeta.step}
                              value={field.field_max ?? ""}
                              onChange={(e) =>
                                updateField(idx, "field_max", e.target.value)
                              }
                              placeholder={minMaxMeta.placeholder}
                            />
                          </Form.Group>
                        </>
                      )}
                    </Row>
                  </div>
                );
              })}

              <Button onClick={submitForm} disabled={buttonDisable}>
                {buttonDisable ? "Processing..." : "Create"}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default DispositionFormsCreate;
